import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { PiArrowsClockwise, PiCaretRight, PiCheckCircle, PiWarning } from 'react-icons/pi'

import MotionDiv from '@/components/common/Motion/MotionDiv'

import { useDriveStore } from '@/store/useDriveStore'

import type { DriveStatus } from '@/types/user'
import type { DriveCopyStatus } from '@/types/expense'

import GoogleDriveIcon from './GoogleDriveIcon'

interface DriveWidgetProps {
  connectionStatus: DriveStatus
  copyState: DriveCopyStatus | null
  pendingCount: number
}

export const DriveWidget = ({ connectionStatus, copyState, pendingCount }: DriveWidgetProps) => {
  const { t } = useTranslation()

  const openSheet = useDriveStore((state) => state.openSheet)

  const connected = connectionStatus === 'connected'
  const attention = connectionStatus === 'revoked' || copyState === 'failed'

  const subhead = !connected
    ? connectionStatus === 'revoked'
      ? t('expenses.drive.status.authNeeded')
      : t('expenses.drive.item.linkSubhead')
    : copyState === 'failed'
      ? t('expenses.drive.status.failed')
      : copyState === 'pending'
        ? t('expenses.drive.status.uploading', { n: pendingCount })
        : copyState === null
          ? t('expenses.drive.status.emptyConnected')
          : t('expenses.drive.status.synced')

  const trailing = !connected
    ? connectionStatus === 'revoked'
      ? <PiWarning className="w-5 h-5 text-amber-500" />
      : <PiCaretRight className="w-4 h-4 text-zinc-500" />
    : copyState === 'pending'
      ? <PiArrowsClockwise className="w-5 h-5 text-zinc-500 animate-spin" />
      : attention
        ? <PiWarning className="w-5 h-5 text-amber-500" />
        : <PiCheckCircle className="w-5 h-5 text-zinc-500" />

  return (
    <MotionDiv className="bg-zinc-100 rounded-2xl p-4 mb-6">
      <motion.div
        whileTap={{ scale: 0.98 }}
        onClick={openSheet}
        className="flex items-center justify-between"
      >
        <div className="flex items-center">
          <div className="flex items-center justify-center min-w-12 min-h-12 rounded-full bg-white mr-5">
            <GoogleDriveIcon className="h-5 w-5" />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="font-medium text-base text-black">
              {t('expenses.drive.item.title')}
            </h3>
            <p className={`text-sm ${attention ? 'text-amber-700' : 'text-zinc-500'}`}>
              {subhead}
            </p>
          </div>
        </div>
        <span className="ml-3">{trailing}</span>
      </motion.div>
    </MotionDiv>
  )
}

export default DriveWidget
